import { WEEKDAYS } from '../zod/calendar.js';
import { berlinYmd, berlinTime, berlinWallToUtc } from './timezone.js';
/** Zivildatum + n Tage (reine Kalenderarithmetik, DST-neutral). */
function addDays(c, n) {
    const t = new Date(Date.UTC(c.y, c.m - 1, c.d + n));
    return { y: t.getUTCFullYear(), m: t.getUTCMonth() + 1, d: t.getUTCDate() };
}
/** Wochentag eines Zivildatums, MO=0 … SU=6 (RRULE-Konvention, nicht JS getDay). */
function civilWeekday(c) {
    return (new Date(Date.UTC(c.y, c.m - 1, c.d)).getUTCDay() + 6) % 7;
}
function toRRuleDate(iso) {
    // → kompakte UTC-Form YYYYMMDDTHHMMSSZ
    return new Date(iso).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
}
function fromRRuleDate(s) {
    const m = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z?$/.exec(s);
    if (m)
        return `${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:${m[6]}.000Z`;
    // UNTIL als reines Datum (bei ganztägigen Serien aus iCal-Abos): inklusiv bis
    // Tagesende — sonst fiele das letzte Vorkommen weg.
    const d = /^(\d{4})(\d{2})(\d{2})$/.exec(s);
    if (d)
        return `${d[1]}-${d[2]}-${d[3]}T23:59:59.000Z`;
    return s;
}
/** Recurrence-Objekt → RRULE-String, z. B. "FREQ=WEEKLY;INTERVAL=1;BYDAY=MO,WE". */
export function buildRRule(r) {
    const parts = [`FREQ=${r.freq}`, `INTERVAL=${r.interval || 1}`];
    if (r.byWeekday?.length)
        parts.push(`BYDAY=${r.byWeekday.join(',')}`);
    if (r.until)
        parts.push(`UNTIL=${toRRuleDate(r.until)}`);
    if (r.count)
        parts.push(`COUNT=${r.count}`);
    return parts.join(';');
}
/** RRULE-String → Recurrence-Objekt (oder null, wenn kein FREQ). */
export function parseRRule(rrule) {
    const map = new Map();
    for (const part of rrule.split(';')) {
        const [k, v] = part.split('=');
        if (k && v)
            map.set(k.toUpperCase(), v);
    }
    const freq = map.get('FREQ');
    if (!freq)
        return null;
    const byday = map.get('BYDAY');
    const until = map.get('UNTIL');
    const count = Number(map.get('COUNT') ?? '');
    return {
        freq,
        interval: Number(map.get('INTERVAL') ?? '1') || 1,
        byWeekday: byday ? byday.split(',').filter((d) => WEEKDAYS.includes(d)) : undefined,
        until: until ? fromRRuleDate(until) : undefined,
        ...(Number.isInteger(count) && count > 0 ? { count } : {}),
    };
}
const FREQ_DE = {
    DAILY: 'Täglich',
    WEEKLY: 'Wöchentlich',
    MONTHLY: 'Monatlich',
    YEARLY: 'Jährlich',
};
const WD_DE = {
    MO: 'Mo', TU: 'Di', WE: 'Mi', TH: 'Do', FR: 'Fr', SA: 'Sa', SU: 'So',
};
/** Menschenlesbare Beschreibung, z. B. "Alle 2 Wochen, Mo/Mi". */
export function describeRRuleDe(rrule) {
    const r = parseRRule(rrule);
    if (!r)
        return 'Einmalig';
    const every = r.interval && r.interval > 1;
    const unit = {
        DAILY: 'Tage', WEEKLY: 'Wochen', MONTHLY: 'Monate', YEARLY: 'Jahre',
    };
    let base = every ? `Alle ${r.interval} ${unit[r.freq]}` : FREQ_DE[r.freq];
    if (r.freq === 'WEEKLY' && r.byWeekday?.length) {
        base += ', ' + r.byWeekday.map((d) => WD_DE[d]).join('/');
    }
    if (r.count)
        base += `, ${r.count}×`;
    return base;
}
/**
 * Entfaltet die Vorkommnisse eines Events im Fenster [windowStart, windowEnd].
 * Ohne rrule → höchstens ein Vorkommen (falls es das Fenster überschneidet).
 * Gibt Start/Ende jeder Instanz als ISO-Strings zurück (aufsteigend).
 */
export function expandOccurrences(input, windowStartISO, windowEndISO, maxCount = 366) {
    const baseStart = new Date(input.startsAt);
    const baseEnd = new Date(input.endsAt);
    const duration = baseEnd.getTime() - baseStart.getTime();
    const winStart = new Date(windowStartISO).getTime();
    const winEnd = new Date(windowEndISO).getTime();
    const overlaps = (s, e) => s <= winEnd && e >= winStart;
    const out = [];
    const push = (s) => {
        const startMs = s.getTime();
        const endMs = startMs + duration;
        if (overlaps(startMs, endMs)) {
            out.push({ start: new Date(startMs).toISOString(), end: new Date(endMs).toISOString() });
        }
    };
    const r = input.rrule ? parseRRule(input.rrule) : null;
    if (!r) {
        push(baseStart);
        return out;
    }
    // Basistermin im Berliner Zivilkalender: Datum + lokale Uhrzeit, die jede
    // Instanz beibehält (DST-stabil).
    const bd = berlinYmd(baseStart);
    const bt = berlinTime(baseStart);
    const baseMs = baseStart.getTime();
    const atWall = (c) => berlinWallToUtc(c.y, c.m, c.d, bt.h, bt.mi, bt.s);
    const untilMs = r.until ? new Date(r.until).getTime() : Number.POSITIVE_INFINITY;
    const limitMs = Math.min(untilMs, winEnd);
    const interval = r.interval || 1;
    let guard = 0;
    const MAX_GUARD = 5000;
    // COUNT zählt ALLE Vorkommen ab Serienbeginn — auch die vor dem Fenster.
    const maxSeries = r.count ?? Number.POSITIVE_INFINITY;
    let seen = 0;
    if (r.freq === 'WEEKLY') {
        const days = (r.byWeekday?.length ? r.byWeekday : [WEEKDAYS[civilWeekday(bd)]]).map((wd) => WEEKDAYS.indexOf(wd));
        // Montag der Basiswoche (Zivil), lokale Uhrzeit beibehalten.
        const monday = addDays(bd, -civilWeekday(bd));
        for (let k = 0; guard++ < MAX_GUARD; k++) {
            const weekMonday = addDays(monday, k * interval * 7);
            // Wochenbeginn (Mitternacht) schon jenseits des Limits → fertig.
            if (berlinWallToUtc(weekMonday.y, weekMonday.m, weekMonday.d, 0, 0, 0).getTime() > limitMs)
                break;
            for (const di of days.slice().sort((a, b) => a - b)) {
                const s = atWall(addDays(weekMonday, di));
                if (s.getTime() < baseMs || s.getTime() > limitMs)
                    continue;
                if (++seen > maxSeries)
                    return out;
                push(s);
                if (out.length >= maxCount)
                    return out;
            }
        }
        return out;
    }
    // DAILY / MONTHLY / YEARLY: jede Instanz k-basiert aus dem Basis-Zivildatum.
    for (let k = 0; guard++ < MAX_GUARD; k++) {
        let cand;
        if (r.freq === 'DAILY') {
            cand = addDays(bd, k * interval);
        }
        else if (r.freq === 'MONTHLY') {
            const total = bd.m - 1 + k * interval;
            const y = bd.y + Math.floor(total / 12);
            const m = (((total % 12) + 12) % 12) + 1;
            if (berlinWallToUtc(y, m, 1, 0, 0, 0).getTime() > limitMs)
                break;
            if (new Date(Date.UTC(y, m - 1, bd.d)).getUTCMonth() !== m - 1)
                continue; // ungültiger Tag (z. B. 31.) → überspringen
            cand = { y, m, d: bd.d };
        }
        else {
            const y = bd.y + k * interval;
            if (berlinWallToUtc(y, bd.m, 1, 0, 0, 0).getTime() > limitMs)
                break;
            if (new Date(Date.UTC(y, bd.m - 1, bd.d)).getUTCMonth() !== bd.m - 1)
                continue; // 29.02. in Nicht-Schaltjahren → überspringen
            cand = { y, m: bd.m, d: bd.d };
        }
        const s = atWall(cand);
        if (s.getTime() > limitMs)
            break;
        if (s.getTime() < baseMs)
            continue;
        if (++seen > maxSeries)
            return out;
        push(s);
        if (out.length >= maxCount)
            return out;
    }
    return out;
}
/**
 * Schlüssel, über den ein Vorkommen und seine Ausnahme zusammenfinden.
 * Ganztägig: Berliner Kalenderdatum — ganztägige Serien wandern über die
 * Sommerzeit in UTC um eine Stunde (00:00Z ↔ 23:00Z Vortag), und EXDATEs aus
 * iCal-Feeds kommen als reines Datum. Mit Uhrzeit: exakter Zeitpunkt.
 */
export function occurrenceKey(iso, allDay) {
    const d = new Date(iso);
    if (allDay) {
        const c = berlinYmd(d);
        return `${c.y}-${String(c.m).padStart(2, '0')}-${String(c.d).padStart(2, '0')}`;
    }
    return d.toISOString();
}
/**
 * Prüft, ob `iso` ein echtes Vorkommen der Serie ist, und liefert dessen
 * kanonischen Start (oder null). Grundlage für „Ausnahme auf nicht
 * existierendes Vorkommen → 422" und für verwaiste Ausnahmen nach Serienänderung.
 */
export function findSeriesOccurrence(input, iso) {
    const t = new Date(iso).getTime();
    if (Number.isNaN(t))
        return null;
    const pad = 36 * 3_600_000;
    const key = occurrenceKey(iso, input.allDay);
    const hits = expandOccurrences(input, new Date(t - pad).toISOString(), new Date(t + pad).toISOString(), 64);
    return hits.find((o) => occurrenceKey(o.start, input.allDay) === key)?.start ?? null;
}
/**
 * Wie expandOccurrences, aber mit Ausnahmen: abgesagte Vorkommen fallen weg,
 * verschobene erscheinen mit ihren neuen Werten — auch wenn sie aus dem
 * Fenster heraus- oder in es hineingeschoben wurden. Ausnahmen, deren
 * Original kein Vorkommen der Serie (mehr) ist, werden ignoriert (verwaist).
 */
export function expandWithOverrides(input, overrides, windowStartISO, windowEndISO) {
    const plain = expandOccurrences(input, windowStartISO, windowEndISO);
    if (!input.rrule || overrides.length === 0) {
        return plain.map((o) => ({ start: o.start, end: o.end, originalStart: o.start, override: null }));
    }
    const byKey = new Map(overrides.map((o) => [occurrenceKey(o.originalStart, input.allDay), o]));
    const out = [];
    for (const o of plain) {
        if (byKey.has(occurrenceKey(o.start, input.allDay)))
            continue; // abgesagt oder verschoben → unten
        out.push({ start: o.start, end: o.end, originalStart: o.start, override: null });
    }
    const winStart = new Date(windowStartISO).getTime();
    const winEnd = new Date(windowEndISO).getTime();
    const duration = new Date(input.endsAt).getTime() - new Date(input.startsAt).getTime();
    for (const ov of overrides) {
        if (ov.kind !== 'moved')
            continue;
        const original = findSeriesOccurrence(input, ov.originalStart);
        if (!original)
            continue; // verwaist
        const start = ov.startsAt ?? original;
        const end = ov.endsAt ?? new Date(new Date(start).getTime() + duration).toISOString();
        const s = new Date(start).getTime();
        const e = new Date(end).getTime();
        if (s <= winEnd && e >= winStart)
            out.push({ start: new Date(s).toISOString(), end: new Date(e).toISOString(), originalStart: original, override: ov });
    }
    out.sort((a, b) => a.start.localeCompare(b.start));
    return out;
}
//# sourceMappingURL=recurrence.js.map